import { LitElement, html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Download, RefreshCw, Info } from 'lucide';
import { api } from '../api.js';
import { alertDialog } from '../utils/confirm-dialog.js';
import { lucideIcon } from '../icon.js';
import { formatDateTime, parseTimestamp } from '../utils/datetime.js';
import { foutUitleg, hallucinatiePreventie } from '../utils/fout-uitleg.js';
import { fmtMs, fmtBytes, fmtGeld, fmtGetal, fmtDuur, stappen, standaardStap, stapKleuren } from '../utils/pijplijn.js';
import { tiCodelists, getSoilLabel, formatKleur, EXTRACTION_PROFILES, extractionProfileFor, getExtractionProfile, LOGPROB_THRESHOLDS } from '@mv/models';
import './mv-table.js';

// Readable label for a SIKB code via a codelist Map (falls back to the raw code).
const code2name = (map, code) => (code == null || code === '' ? '' : (map?.get?.(code) ?? String(code)));

// Idem, maar voor de peilbuis-codelijsten: die zijn label→code (omgekeerd t.o.v. de
// laag-lijsten), dus zoeken we op waarde. Valt terug op de ruwe code.
const label4code = (map, code) => {
  if (code == null || code === '') return '';
  for (const [label, c] of (map?.entries?.() ?? [])) if (String(c) === String(code)) return label;
  return String(code);
};

// 1/0/null uit de DB → leesbaar.
const yesNo = (v) => (v === 1 || v === true ? 'ja' : v === 0 || v === false ? 'nee' : '—');

// Custom chevron (replaces the native arrow) so we can place it closer to the text.
const CHEVRON = "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='14'%20height='14'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%236c757d'%20stroke-width='2.5'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpolyline%20points='6%209%2012%2015%2018%209'/%3E%3C/svg%3E\")";
const SELECT_BASE = `width:100%; padding:0.65rem 2rem 0.65rem 0.9rem; border:1px solid var(--mv-border); border-radius:var(--mv-radius-sm); font-size:0.9rem; color:var(--mv-text); background-color:var(--mv-surface,#fff); outline:none; box-sizing:border-box; font-family:var(--mv-font); cursor:pointer; -webkit-appearance:none; appearance:none; background-image:${CHEVRON}; background-repeat:no-repeat; background-position:right 10px center; background-size:14px;`;

/**
 * Analysis — Sysadmin-only pipeline observability across ALL companies.
 * Shows every job (audio → transcript → uitkomst) with per-step timestamps;
 * a detail view shows the exact input/output of each step (transcript, ruwe AI-JSON,
 * en de weggeschreven uitkomst) + audio playback. Data: GET /api/admin/analysis/*.
 *
 * Elke rij is één run van één van de vijf extractiemodellen (kolom 'Analyse'), dus de
 * uitkomst is niet altijd een boorprofiel — het kan ook een peilbuis, watermonster of
 * afwerking zijn. Validatieruns staan hier bewust NIET tussen: die draaien tegen
 * DB_VALIDATION (geen bedrijf) en worden niet in processing_runs gelogd.
 */
export class AnalysisPage extends LitElement {
  createRenderRoot() { return this; }

  /**
   * Welke kolommen er zijn, en welke standaard aan staan.
   *
   * De meetkolommen staan standaard UIT. Ze zijn er voor een vraag die je af en toe stelt --
   * "waarom duurde die run 40 seconden", "wat kost een dag veldwerk" -- en acht kolommen erbij
   * maakt de tabel onleesbaar voor wie alleen wil zien of er iets misging. Wie ze nodig heeft
   * zet ze aan; de keuze wordt per gebruiker bewaard.
   *
   * Ze zijn pas gevuld sinds het pijplijn-logboek bestaat. Een oudere run toont er een streepje,
   * en dat betekent 'niet vastgelegd' -- niet nul.
   */
  static COLUMN_OPTIONS = [
    { id: 'company_name', label: 'Bedrijf' },
    { id: 'project_name', label: 'Project' },
    { id: 'trigger', label: 'Type' },
    { id: 'uploaded_by_email', label: 'Gebruiker' },
    { id: 'created_at', label: 'Tijdstip' },
    { id: 'analysis', label: 'Analyse' },
    { id: 'status', label: 'Status' },
    { id: 'pogingen', label: 'Aantal pogingen', default: false },
    { id: 'zekerheid', label: 'Zekerheid transcript', default: false },
    // Vervangt de oude kolom 'Duur'. Die mat bij een run precies de extractie plus het
    // wegschrijven -- dezelfde waarde als 'Duur extractie', op twee tienden na -- en bij een
    // opname zonder run iets heel anders, namelijk upload tot klaar. Eén kop boven twee
    // betekenissen; nu staat er één waarde met één betekenis.
    { id: 'ms_totaal', label: 'Totale duur' },
    { id: 'tokens', label: 'Tokens', default: false },
    { id: 'kosten', label: 'Kosten (schatting)', default: false },
    { id: 'priority', label: 'Priority', default: false },
    { id: 'ms_transcriptie', label: 'Duur transcript', default: false },
    { id: 'ms_extractie', label: 'Duur extractie', default: false },
  ];

  static DEFAULT_COLUMNS = this.COLUMN_OPTIONS.filter((c) => c.default !== false).map((c) => c.id);

  /**
   * De `.v2` is er omdat 'Duur' is vervangen door 'Totale duur'. Een opgeslagen keuze uit v1
   * noemt `sec_total` (die er niet meer is, dus die filteren we weg) en kent `ms_totaal` niet --
   * waardoor de vervanger bij iedereen die ooit iets aan de kolommen veranderde UIT zou staan,
   * precies andersom dan bedoeld. Een nieuwe sleutel geeft iedereen de nieuwe standaard; de
   * prijs is dat een eerdere kolomkeuze eenmalig terugvalt.
   */
  static COLUMNS_PREF = 'columns.adminAnalysis.v2';

  static properties = {
    jobs: { type: Array },
    loading: { type: Boolean },
    error: { type: String },
    companyFilter: { type: String },
    projectFilter: { type: String },
    userFilter: { type: String },
    statusFilter: { type: String },
    profileFilter: { type: String },   // op extractiemodel (kolom 'Analyse')
    selected: { type: Object },     // detail payload
    detailLoading: { type: Boolean },
    reprocessing: { type: Boolean },
    audioUrl: { type: String },
    step: { type: String },        // welke pijplijnstap in het detail openstaat
    tab: { type: String },         // tabblad binnen de extractiestap
    vocabOpen: { type: Boolean },  // woordenlijst ingeklapt of volledig
    copied: { type: String },      // welke kopieerknop zojuist is gebruikt
    visibleColumns: { type: Array },
    showValidation: { type: Boolean }, // ook de rijen van /admin/validation tonen
    periode: { type: String },         // '24u' | '7d' | '30d' | 'alles'
    afgekapt: { type: Boolean },       // de backend gaf niet alle runs (vangrail geraakt)
  };

  /** Periodes voor de lijst. Elke verwerking is een rij, dus 'alles' kan duizenden rijen zijn. */
  static PERIODES = [
    { id: '24u', label: 'Laatste 24 uur', uren: 24 },
    { id: '7d', label: 'Laatste 7 dagen', uren: 24 * 7 },
    { id: '30d', label: 'Laatste 30 dagen', uren: 24 * 30 },
    { id: 'alles', label: 'Alles', uren: null },
  ];

  constructor() {
    super();
    this.jobs = [];
    this.loading = false;
    this.error = null;
    this.companyFilter = 'all';
    this.projectFilter = 'all';
    this.userFilter = 'all';
    this.statusFilter = 'all';
    this.profileFilter = 'all';
    this.selected = null;
    this.detailLoading = false;
    this.reprocessing = false;
    this.audioUrl = null;
    this.step = null;
    this.tab = 'prompt';
    this.vocabOpen = false;
    this.copied = null;
    this.visibleColumns = AnalysisPage.DEFAULT_COLUMNS;
    this.showValidation = false;
    this.periode = '7d';
    this.afgekapt = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
    this._loadColumnPrefs();
    this._onEsc = (e) => { if (e.key === 'Escape' && this.selected) this.closeDetail(); };
    document.addEventListener('keydown', this._onEsc);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onEsc);
    this._revokeAudio();
    super.disconnectedCallback();
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      const p = AnalysisPage.PERIODES.find((x) => x.id === this.periode);
      const since = p?.uren ? new Date(Date.now() - p.uren * 3_600_000).toISOString() : null;
      const data = await api.getAnalysisJobs(p?.uren ? 2000 : 5000, { validation: this.showValidation, since });
      this.jobs = data.jobs || [];
      this.afgekapt = !!data.afgekapt;
      // Stond het bedrijfsfilter op de validatieset en gaat die nu uit beeld, dan bleef er een
      // lege tabel over met een filter dat je niet meer terug kunt zetten.
      if (this.companyFilter !== 'all' && !this.companyOptions.includes(this.companyFilter)) {
        this.onCompanyChange('all');
      }
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }
  }

  _revokeAudio() {
    // Sinds de audio een gewone URL is valt er niets meer vrij te geven; het legen van de
    // verwijzing blijft nodig zodat een volgende opname niet even de vorige laat horen.
    this.audioUrl = null;
  }

  /** Hoeveel verwerkingen er nu in beeld zijn, en wat ze samen ongeveer kostten. */
  _renderTelling() {
    if (this.loading && !this.jobs.length) return '';
    const rijen = this.filtered;
    const runs = rijen.filter((j) => j.run_id != null).length;
    const kosten = rijen.reduce((s, j) => s + (typeof j.kosten === 'number' ? j.kosten : 0), 0);
    const zonder = rijen.filter((j) => j.run_id != null && typeof j.kosten !== 'number').length;
    return html`
      <div style="display:flex; flex-wrap:wrap; gap:6px 18px; align-items:baseline; margin-bottom:0.75rem; font-size:0.88rem; color:var(--mv-text-secondary, #555);">
        <span><b style="color:var(--mv-text); font-variant-numeric:tabular-nums;">${fmtGetal(runs)}</b> verwerkingen${rijen.length > runs ? html` + ${fmtGetal(rijen.length - runs)} zonder logboek` : ''}</span>
        <span title="Opgeteld uit het gemeten verbruik per verwerking, inclusief afgekeurde pogingen en het priority-tarief. Een schatting: de prijzen komen uit een handmatig bijgehouden prijslijst.">
          Geschatte kosten <b style="color:var(--mv-text); font-variant-numeric:tabular-nums;">${fmtGeld(kosten) ?? '$0'}</b>${zonder ? ` (${zonder} zonder kostenlogboek)` : ''}
        </span>
        ${this.afgekapt ? html`<span style="color:#b45309;">Niet alles getoond: kies een kortere periode.</span>` : ''}
      </div>`;
  }

  get filtered() {
    return this.jobs.filter(j =>
      (this.companyFilter === 'all' || j.company_name === this.companyFilter) &&
      (this.projectFilter === 'all' || (j.project_name || '—') === this.projectFilter) &&
      (this.userFilter === 'all' || (j.uploaded_by_email || '—') === this.userFilter) &&
      (this.statusFilter === 'all' || j.status === this.statusFilter) &&
      (this.profileFilter === 'all' || this._profileOf(j) === this.profileFilter));
  }

  /**
   * Het extractiemodel van een job = (domein × standaard). Oude analyses hebben geen domein
   * en geen standaard vastgelegd; die zijn per definitie met het lagen-NEN 5104-model gedaan.
   */
  _profileOf(j) { return j.ai_kind || extractionProfileFor(j.domain, j.soil_standard); }

  // Distinct option values — only those that actually occur in the jobs we have.
  get companyOptions() {
    return [...new Set(this.jobs.map(j => j.company_name).filter(Boolean))].sort();
  }
  // Projects are scoped to the selected company (cascading).
  get projectOptions() {
    const scope = this.companyFilter === 'all' ? this.jobs : this.jobs.filter(j => j.company_name === this.companyFilter);
    return [...new Set(scope.map(j => j.project_name || '—'))].sort();
  }
  get userOptions() {
    const scope = this.companyFilter === 'all' ? this.jobs : this.jobs.filter(j => j.company_name === this.companyFilter);
    return [...new Set(scope.map(j => j.uploaded_by_email || '—'))].sort();
  }

  onCompanyChange(v) {
    this.companyFilter = v;
    // Reset narrower filters that may no longer be valid for the new company.
    this.projectFilter = 'all';
    this.userFilter = 'all';
  }

  /** De kolomkeuze van deze gebruiker. Lukt dit niet, dan staat gewoon de standaard aan. */
  async _loadColumnPrefs() {
    try {
      const prefs = await api.getPreferences();
      const saved = prefs?.[AnalysisPage.COLUMNS_PREF];
      // Een LEGE lijst is een geldige keuze (alles uit) en mag niet als 'niets ingesteld'
      // tellen -- vandaar de typecheck en niet een check op lengte. Onbekende ids vallen weg,
      // zodat een hernoemde kolom geen spookkolom achterlaat.
      if (Array.isArray(saved)) {
        const bekend = new Set(AnalysisPage.COLUMN_OPTIONS.map((c) => c.id));
        this.visibleColumns = saved.filter((id) => bekend.has(id));
      }
    } catch (_) { /* voorkeuren zijn comfort, geen voorwaarde */ }
  }

  async _saveColumnPrefs(zichtbaar) {
    this.visibleColumns = zichtbaar;
    try { await api.setPreference(AnalysisPage.COLUMNS_PREF, zichtbaar); }
    catch (e) { console.error('kolomvoorkeur opslaan mislukt:', e); }
  }

  /**
   * Een meetwaarde in de tabel. Null is 'niet vastgelegd' en krijgt een streepje met uitleg --
   * een nul zou lezen als 'het kostte niets' of 'het duurde niet', en dat is iets anders.
   */
  _meting(waarde, opmaak, uitleg = 'Niet vastgelegd bij deze verwerking') {
    if (waarde == null) return html`<span title=${uitleg} style="color:var(--mv-text-sub,#adb5bd);">—</span>`;
    return html`<span style="font-variant-numeric:tabular-nums; color:var(--mv-text);">${opmaak(waarde)}</span>`;
  }

  fmtDate(d) {
    return d ? formatDateTime(d, { month: 'short' }) : '—';
  }

  // Human-readable grondsoort for a layer: soil + admixtures (gradatie + toevoeging).
  _soilText(l) {
    const soil = getSoilLabel?.(l.soil_type) || code2name(tiCodelists.bodemsoort, l.soil_type) || '—';
    const parts = [];
    for (const [t, g] of [[l.toevoeging1, l.gradatie1], [l.toevoeging2, l.gradatie2]]) {
      if (!t) continue;
      const grad = code2name(tiCodelists.gradatie, g);
      const toev = code2name(tiCodelists.toevoeging, t);
      parts.push([grad, toev].filter(Boolean).join(' '));
    }
    return parts.length ? `${parts.join(', ')} ${soil}`.trim() : soil;
  }

  // BZB (bijzondere bestanddelen) summary for a layer.
  _bzbText(l) {
    let arr = [];
    try { arr = JSON.parse(l.bzb || '[]'); } catch { arr = []; }
    if (!Array.isArray(arr) || !arr.length) return '';
    return arr.map((b) => {
      const name = code2name(tiCodelists.LaSpecialCompounds, b.code) || b.type || b.code || '';
      const grad = code2name(tiCodelists.LaSpecialCompoundsGradiation, b.gradation);
      return [name, grad].filter(Boolean).join(' ');
    }).filter(Boolean).join(', ');
  }

  _kleurText(l) {
    return formatKleur?.({ ti_color_main: l.kleur_main, ti_color_shade: l.kleur_shade, ti_color_sub: l.kleur_sub }) || '';
  }

  // White background + custom chevron placed close to the text.
  get _selectStyle() { return SELECT_BASE; }

  // A labelled dropdown showing only values that occur in the data ("Alle …" = no filter).
  filterSelect(label, value, options, onChange) {
    const allLabel = { Bedrijf: 'Alle bedrijven', Project: 'Alle projecten', Gebruiker: 'Alle gebruikers' }[label] || `Alle ${label.toLowerCase()}`;
    return html`
      <div style="min-width:180px;">
        <select .value=${value} @change=${(e) => onChange(e.target.value)} title=${label} style=${this._selectStyle}>
          <option value="all">${allLabel}</option>
          ${options.map((o) => html`<option value=${o} ?selected=${o === value}>${o}</option>`)}
        </select>
      </div>`;
  }

  statusBadge(status, slow) {
    const map = {
      processed: ['rgba(34,197,94,0.15)', '#15803d', 'Verwerkt'],
      error: ['rgba(220,53,69,0.12)', 'var(--mv-danger,#dc3545)', 'Fout'],
      transcribed: ['rgba(59,130,246,0.12)', '#1d4ed8', 'Getranscribeerd'],
      uploaded: ['var(--mv-bg,#f1f3f5)', 'var(--mv-text-sub,#6c757d)', 'Geüpload'],
      processing: ['rgba(245,158,11,0.15)', '#b45309', 'Bezig'],
    };
    const [bg, fg, label] = map[status] || ['var(--mv-bg,#f1f3f5)', 'var(--mv-text-sub,#6c757d)', status || '—'];
    return html`<span style="display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:999px; font-size:0.78rem; font-weight:700; background:${bg}; color:${fg};">
      ${label}${slow ? html` <span title="Trage verwerking" style="color:#b45309;">⏱</span>` : ''}
    </span>`;
  }

  // Human label for a log entry's trigger.
  triggerLabel(t) {
    return { upload: 'Upload', reprocess: 'Opnieuw verwerkt', historisch: 'Historisch', verwerking: 'Verwerking', validation: 'Validatie' }[t] || '—';
  }

  // Badge voor het extractiemodel dat deze analyse heeft gedraaid — één van de vijf.
  // Elk model een eigen kleur, zodat je in de lijst meteen ziet welk model waar draait.
  analysisBadge(profileId) {
    if (AnalysisPage.AI_SOORTEN[profileId]) return this._aiBadge(profileId);
    const p = getExtractionProfile(profileId);
    const [bg, fg] = {
      nen5104:      ['var(--mv-bg,#f1f3f5)', 'var(--mv-text-sub,#6c757d)'],
      nen6693:      ['rgba(37,99,235,0.12)', '#1d4ed8'],
      peilbuis:     ['rgba(13,148,136,0.14)', '#0f766e'],
      watermonster: ['rgba(8,145,178,0.14)', '#0e7490'],
      afwerking:    ['rgba(217,119,6,0.14)', '#b45309'],
    }[p.id];
    return html`<span title=${p.description}
      style="display:inline-flex; align-items:center; padding:2px 9px; border-radius:999px; font-size:0.75rem; font-weight:700; background:${bg}; color:${fg}; white-space:nowrap;">${p.label}</span>`;
  }

  get columns() {
    return [
      // Bedrijf en Project vast: van de acht kolommen hadden er vier een breedte en vier niet,
      // en die vier verdeelden samen alle restruimte naar rato van hun inhoud. Bij korte namen
      // ("Local Dev", "test-3") hoopte dat zich op in Project, met een gat van honderden pixels
      // tot de volgende kolom. Gebruiker en Tijdstip blijven vrij; daar mág de rek zitten,
      // want een e-mailadres en een datum lopen echt in lengte uiteen.
      { id: 'company_name', title: 'Bedrijf', width: '200px', render: (j) => html`<span style="font-weight:600; color:var(--mv-text);">${j.company_name}</span>${j.toegang === 'meekijken'
          ? html` <span title="Geen lid; dit bedrijf heeft meekijken door systeembeheerders aangezet" style="font-size:0.72rem; font-weight:600; color:var(--mv-text-secondary,#6c757d);">· meekijken</span>` : ''}` },
      { id: 'project_name', title: 'Project', width: '240px', render: (j) => j.project_name || html`<span style="color:var(--mv-text-sub,#6c757d);">—</span>` },
      { id: 'trigger', title: 'Type', width: '140px', render: (j) => {
          if (j.ai_kind) return html`<span style="font-size:0.82rem; font-weight:600; color:var(--mv-text);">${this._aiSoort(j.ai_kind).type}</span>`;
          const muted = j.trigger === 'historisch' || !j.trigger;
          return html`<span style="font-size:0.82rem; font-weight:600; color:${muted ? 'var(--mv-text-sub,#adb5bd)' : 'var(--mv-text)'};">${this.triggerLabel(j.trigger)}</span>`;
        } },
      { id: 'uploaded_by_email', title: 'Gebruiker', render: (j) => j.uploaded_by_email || html`<span style="color:var(--mv-text-sub,#adb5bd);" title="Geen gebruiker vastgelegd (oude opname)">—</span>` },
      { id: 'created_at', title: 'Tijdstip', render: (j) => this.fmtDate(j.created_at) },
      { id: 'analysis', title: 'Analyse', width: '150px', render: (j) => this.analysisBadge(this._profileOf(j)) },
      // Status plus REDEN. Een rode pil zonder tekst laat je de rij openen om te ontdekken
      // wat er misging -- en bij veertig rijen doe je dat niet meer. De korte typering staat
      // dus in de lijst; de volledige melding hangt eronder als tooltip en in het detail.
      { id: 'status', title: 'Status', width: '230px', render: (j) => {
          const uitleg = j.status === 'error' ? foutUitleg(j.error_message) : null;
          // Tegengehouden door de hallucinatie preventie: geen storing maar een bewuste keuze,
          // dus geen rode 'Fout' maar een eigen label.
          if (uitleg?.preventie) {
            return html`
              <div style="display:flex; flex-direction:column; align-items:flex-start; gap:3px;">
                ${this._preventieBadge(uitleg.kort)}
              </div>`;
          }
          return html`
            <div style="display:flex; flex-direction:column; align-items:flex-start; gap:3px;">
              ${this.statusBadge(j.status, j.sec_total != null && j.sec_total > 60)}
              ${j.status === 'error' ? (uitleg
                ? html`<span title=${uitleg.ruw}
                    style="font-size:0.72rem; line-height:1.35; color:var(--mv-danger,#dc3545); display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;"
                    >${uitleg.kort}</span>`
                : html`<span style="font-size:0.72rem; color:var(--mv-text-sub,#adb5bd);"
                    title="Deze run is mislukt voordat we de reden vastlegden">Reden niet vastgelegd</span>`) : ''}
            </div>`;
        } },
      // ── Meetkolommen, standaard uit ────────────────────────────────────────
      { id: 'pogingen', title: 'Pogingen', width: '110px', render: (j) =>
          this._meting(j.pogingen, (v) => v, 'Pogingen worden pas vastgelegd sinds het pijplijn-logboek') },
      { id: 'zekerheid', title: 'Zekerheid', width: '120px', render: (j) => {
          const grens = LOGPROB_THRESHOLDS[j.transcript_model] ?? Math.min(...Object.values(LOGPROB_THRESHOLDS));
          const zorg = typeof j.transcript_logprob === 'number' && j.transcript_logprob < grens;
          if (j.transcript_logprob == null) return this._meting(null, (v) => v);
          return html`<span title="Gemiddelde log-waarschijnlijkheid; 0 = zeker. Grens ${grens}."
            style="font-variant-numeric:tabular-nums; color:${zorg ? 'var(--mv-danger,#dc3545)' : 'var(--mv-text)'};"
            >${Number(j.transcript_logprob).toFixed(2)}</span>`;
        } },
      { id: 'ms_totaal', title: 'Totale duur', width: '130px', render: (j) => {
          if (j.ms_totaal == null) return this._meting(null, fmtMs);
          const traag = j.ms_totaal > 60_000;
          return html`<span title="Van het begin van de transcriptie tot het wegschrijven, inclusief wachttijd in de rij."
            style="font-variant-numeric:tabular-nums; font-weight:600; color:${traag ? '#b45309' : 'var(--mv-text)'};"
            >${fmtMs(j.ms_totaal)}</span>`;
        } },
      { id: 'tokens', title: 'Tokens', width: '110px', render: (j) => this._meting(j.tokens, fmtGetal) },
      { id: 'kosten', title: 'Kosten', width: '120px', render: (j) =>
          this._meting(j.kosten, fmtGeld, 'Kosten worden pas geschat sinds het pijplijn-logboek') },
      { id: 'priority', title: 'Priority', width: '110px', render: (j) => {
          if (!j.priority) return this._meting(null, (x) => x);
          const tekst = { priority: 'Ja', gevraagd: 'Geweigerd', standaard: 'Nee' }[j.priority] || j.priority;
          const uitleg = {
            priority: 'OpenAI verwerkte de extractie met priority: sneller, ca. 1,75x het tarief.',
            gevraagd: 'Priority gevraagd maar niet geleverd: gewoon tarief.',
            standaard: 'Geen priority gevraagd: gewoon tarief.',
          }[j.priority] || '';
          return html`<span title=${uitleg} style="font-weight:${j.priority === 'priority' ? 600 : 400};">${tekst}</span>`;
        } },
      { id: 'ms_transcriptie', title: 'Duur transcript', width: '140px', render: (j) => this._meting(j.ms_transcriptie, fmtMs) },
      { id: 'ms_extractie', title: 'Duur extractie', width: '130px', render: (j) => this._meting(j.ms_extractie, fmtMs) },
    ].filter((c) => this.visibleColumns.includes(c.id));
  }

  async openDetail(job) {
    this.selected = { _loading: true, job };
    this.detailLoading = true;
    // Een nieuwe rij begint schoon: anders opent de volgende opname op het tabblad waar je de
    // vorige hebt achtergelaten, en dat is zelden de stap waar het deze keer om gaat.
    this.step = null;
    this.tab = 'prompt';
    this.vocabOpen = false;
    this.copied = null;
    this._revokeAudio();
    try {
      const detail = await api.getAnalysisJobDetail(job.company_id, job.recording_id, job.run_id);
      // Een AI-aanroep buiten de pijplijn: geen audio, eigen popup (renderAiKop/renderAiDetail).
      if (detail?.kind === 'ai') {
        this.selected = { ...detail, _toegang: job.toegang || 'lid' };
        this.audioUrl = null;
        this._laadScanPaginas(job.company_id, job.recording_id, detail.call?.pages || 0);
        return;
      }
      // Meekijken is alleen-lezen: dan geen 'Opnieuw verwerken' (de backend weigert het ook).
      this.selected = { ...detail, _toegang: job.toegang || 'lid' };
      // Directe URL: geen ophaalslag nodig, en het ⋮-menu van de browser kan er een fatsoenlijk
      // bestand van maken (zie api.analysisAudioUrl).
      // Opname verwijderd (bv. met zijn project): de verwerking is te zien, de audio niet meer.
      this.audioUrl = detail?.recording?.deleted ? null : api.analysisAudioUrl(job.company_id, job.recording_id);
    } catch (e) {
      alertDialog({ title: 'Openen mislukt', message: e.message });
      this.selected = null;
    } finally {
      this.detailLoading = false;
    }
  }

  closeDetail() { this.selected = null; this._revokeAudio(); this._ruimScanPaginasOp(); }

  /** De gescande pagina's van een AI-aanroep ophalen (met inlogkop) en als object-URL tonen. */
  async _laadScanPaginas(companyId, jobId, aantal) {
    this._ruimScanPaginasOp();
    if (!aantal) return;
    const urls = await Promise.all(Array.from({ length: aantal }, (_, i) =>
      api.analysisAiPage(companyId, jobId, i + 1).catch(() => null)));
    // Intussen een andere rij geopend: die pagina's horen er niet bij.
    if (this.selected?.call?.pages !== aantal || !this.selected) { urls.forEach((u) => u && URL.revokeObjectURL(u)); return; }
    this.scanPaginas = urls;
    this.requestUpdate();
  }

  _ruimScanPaginasOp() {
    (this.scanPaginas || []).forEach((u) => u && URL.revokeObjectURL(u));
    this.scanPaginas = [];
  }

  /**
   * Re-run AI extraction on the selected job's existing transcript, scoped to its
   * own company. Reloads the detail (new output/status) and the job list when done.
   */
  async reprocessJob() {
    const d = this.selected;
    if (!d?.recording || !d?.company || this.reprocessing) return;
    this.reprocessing = true;
    try {
      await api.reprocessAnalysisJob(d.company.id, d.recording.id);
      await this.openDetail({ company_id: d.company.id, recording_id: d.recording.id });
      this.load();
    } catch (e) {
      alertDialog({ title: 'Opnieuw verwerken mislukt', message: e.message });
    } finally {
      this.reprocessing = false;
    }
  }

  async downloadAudio() {
    const d = this.selected;
    if (!d?.recording) return;
    try {
      const url = this.audioUrl || api.analysisAudioUrl(d.company.id, d.recording.id);
      const a = document.createElement('a');
      a.href = url; a.download = d.recording.filename || 'audio.m4a';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alertDialog({ title: 'Downloaden mislukt', message: e.message }); }
  }

  // ── Detail view ─────────────────────────────────────────────────────────

  // Small config line (audio→text model, text→boorprofiel model, prompt version)
  // that produced this boorprofiel — mirrors the /admin/validation header.
  // ── Uitkomst per model ───────────────────────────────────────────────────
  // Een run van het lagen-model levert een boorprofiel; de andere drie modellen schrijven
  // hun eigen slice naar de doelboring. Elk krijgt hier zijn eigen weergave.

  _kv(rows) {
    const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—');
    if (!shown.length) return html`<span style="color:var(--mv-text-sub,#6c757d); font-size:0.82rem;">Niets ingevuld.</span>`;
    return html`<div style="display:grid; grid-template-columns:auto 1fr; gap:2px 12px; font-size:0.82rem;">
      ${shown.map(([k, v]) => html`
        <span style="color:var(--mv-text-sub,#6c757d);">${k}</span><span style="color:var(--mv-text);">${v}</span>`)}
    </div>`;
  }

  _renderPeilbuizen(rows) {
    if (!rows?.length) return html`<div style="color:var(--mv-text-sub,#6c757d); font-size:0.85rem;">Geen peilbuizen weggeschreven.</div>`;
    return html`<div style="display:flex; flex-direction:column; gap:12px;">
      ${rows.map((p) => html`
        <div>
          <div style="font-weight:700; color:var(--mv-text); margin-bottom:6px;">${p.name || '(naamloos)'}</div>
          ${this._kv([
            ['Filter', p.filter_from_cm != null || p.filter_to_cm != null ? `${p.filter_from_cm ?? '?'}–${p.filter_to_cm ?? '?'} cm` : null],
            ['Materiaal', label4code(tiCodelists.peilbuisMateriaal, p.material_type)],
            ['Diameter', p.diameter_mm != null ? `${p.diameter_mm} mm` : null],
            ['Binnendiameter', p.inner_diameter_mm != null ? `${p.inner_diameter_mm} mm` : null],
            ['Doorloop', label4code(tiCodelists.peilbuisDoorloop, p.flow_type)],
            ['Referentievlak', label4code(tiCodelists.peilbuisReferentievlak, p.reference_point_type)],
            ['Filterkous', p.filter_sieve_used == null ? null : yesNo(p.filter_sieve_used)],
            ['Afgepompt', p.unwatered == null ? null : yesNo(p.unwatered)],
            ['Gespoeld', p.rinsed == null ? null : yesNo(p.rinsed)],
            ['pH', p.ph], ['EGV', p.ec != null ? `${p.ec} µS/cm` : null],
            ['Temperatuur', p.temperature != null ? `${p.temperature} °C` : null],
            ['Afgepompt volume', p.pumped_l != null ? `${p.pumped_l} l` : null],
          ])}
        </div>`)}
    </div>`;
  }

  _renderWatermonsters(list) {
    if (!Array.isArray(list) || !list.length) return html`<div style="color:var(--mv-text-sub,#6c757d); font-size:0.85rem;">Geen watermonsters weggeschreven.</div>`;
    return html`<div style="display:flex; flex-direction:column; gap:12px;">
      ${list.map((w, i) => html`
        <div>
          <div style="font-weight:700; color:var(--mv-text); margin-bottom:6px;">${w.name || `Monster ${i + 1}`}</div>
          ${this._kv([
            ['Datum', w.date], ['Matrix', w.matrix], ['Opbrengst', w.yield], ['Pomptype', w.pumpType],
            ['pH', w.ph], ['EC', w.ec != null ? `${w.ec} µS/cm` : null],
            ['Temperatuur', w.temperature != null ? `${w.temperature} °C` : null],
            ['Zuurstof', w.oxygen != null ? `${w.oxygen} mg/l` : null],
            ['Redox', w.redox != null ? `${w.redox} mV` : null],
            ['Troebelheid', w.turbiditySample != null ? `${w.turbiditySample} NTU` : null],
            ['Helderheid', w.clarity],
            ['Kleur', [w.colourShade, w.colourMain, w.colourSub].filter(Boolean).join(' ') || null],
            ['Opmerking', w.remark],
          ])}
          ${w.bottles?.length ? html`
            <div style="margin-top:6px; font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">
              ${w.bottles.length} fles${w.bottles.length === 1 ? '' : 'sen'}:
              ${w.bottles.map((b) => [b.barcode, b.bottleType, b.analysisPackage].filter(Boolean).join(' · ') || '(leeg)').join(' | ')}
            </div>` : ''}
        </div>`)}
    </div>`;
  }

  _renderAfwerking(afw) {
    if (!afw || (!afw.fillings?.length && !afw.casings?.length && !afw.topCompletions?.length)) {
      return html`<div style="color:var(--mv-text-sub,#6c757d); font-size:0.85rem;">Geen afwerking weggeschreven.</div>`;
    }
    const line = (label, body) => html`<div style="margin-bottom:6px;">
      <span style="font-size:0.75rem; font-weight:700; color:var(--mv-text-sub,#6c757d);">${label}</span>
      <div style="font-size:0.82rem; color:var(--mv-text);">${body}</div></div>`;
    return html`
      ${afw.fillings?.length ? line('VULTRAJECTEN', html`${afw.fillings.map((f) => html`
        <div>${f.van ?? '?'}–${f.tot ?? '?'} cm · ${f.materiaal || '—'}${f.opmerking ? ` · ${f.opmerking}` : ''}</div>`)}`) : ''}
      ${afw.casings?.length ? line('CASINGS', html`${afw.casings.map((c) => html`
        <div>${c.van ?? '?'}–${c.tot ?? '?'} cm · achtergebleven: ${c.leftInGround || '—'}</div>`)}`) : ''}
      ${afw.topCompletions?.length ? line('BOVENAFWERKING', html`${afw.topCompletions.map((t) => html`<div>${t.type || '—'}</div>`)}`) : ''}`;
  }

  /**
   * Wat het transcriptiemodel zelf over zijn werk zei: de gehoorde taal en zijn zekerheid.
   *
   * Dit staat er om de drempel voor 'onzeker' te kunnen ijken. Zolang die op een schatting
   * berust is elk getal hier meer waard dan een mening: kijk bij een stuk of twintig geslaagde
   * opnamen waar de zekerheid ligt, en bij de mislukte, en dan weet je waar de grens hoort.
   *
   * Alleen whisper-1 geeft de taal terug (via verbose_json); bij de gpt-4o-modellen blijft dat
   * veld leeg en is de zekerheid het enige signaal.
   */
  transcriptKwaliteit(r) {
    const lp = r?.transcript_logprob;
    const taal = r?.transcript_language;
    const model = r?.transcript_model;
    if (lp == null && !taal && !model) return '';
    // De grens hangt af van het model, dus zonder de modelnaam is een getal hier niet te lezen.
    // Onbekend model: de voorzichtigste grens, net als de backend doet.
    const grens = LOGPROB_THRESHOLDS[model] ?? Math.min(...Object.values(LOGPROB_THRESHOLDS));
    const zorgelijk = typeof lp === 'number' && lp < grens;
    return html`
      <div style="display:flex; flex-wrap:wrap; gap:6px 14px; align-items:center; margin-bottom:8px;
                  font-size:0.78rem; color:var(--mv-text-sub,#6c757d);">
        ${model ? html`<span title="Het model dat deze tekst heeft gemaakt. Na een afgekeurd transcript is dat het terugvalmodel, niet het ingestelde model.">
          Transcriptie door <strong style="color:var(--mv-text);">${model}</strong></span>` : ''}
        ${taal ? html`<span>Model hoorde <strong style="color:var(--mv-text);">${taal}</strong></span>` : ''}
        ${lp == null ? '' : html`
          <span title="Gemiddelde log-waarschijnlijkheid. 0 = zeker; hoe negatiever, hoe meer het model gokte. Grens voor ${model || 'dit model'}: ${grens}.">
            Zekerheid
            <strong style="color:${zorgelijk ? 'var(--mv-danger,#dc3545)' : 'var(--mv-text)'};">
              ${Number(lp).toFixed(2)}
            </strong>
            <span style="opacity:0.7;">(grens ${grens})</span>
          </span>`}
      </div>`;
  }

  // ── Pijplijn-detail: opmaak-hulpjes ──────────────────────────────────────
  //
  // Alles wat hier getoond wordt komt uit de API. Staat een gegeven er niet in, dan zegt de
  // pagina dát -- "niet vastgelegd" -- in plaats van een leeg vakje of een nul. Die twee lezen
  // hetzelfde en betekenen iets heel anders: een run van vóór deze meting heeft geen pogingen
  // vastgelegd, een run met één poging heeft er één.

  /** Een waarde die er niet is, met de reden in de tooltip. */
  _leeg(reden = 'Niet vastgelegd bij deze verwerking') {
    return html`<span title=${reden} style="color:var(--mv-text-sub,#adb5bd);">— niet vastgelegd</span>`;
  }

  /** Het kv-raster uit het ontwerp: label klein en grijs, waarde eronder. */
  _kvGrid(items, kolommen = 4) {
    return html`
      <div style="display:grid; grid-template-columns:repeat(${kolommen},minmax(0,1fr)); gap:1px;
                  background:var(--mv-border,#e9ecef); border:1px solid var(--mv-border,#e9ecef);
                  border-radius:10px; overflow:hidden;">
        ${items.map(([k, v, mono]) => html`
          <div style="background:var(--mv-surface,#fff); padding:10px 14px; display:flex; flex-direction:column; gap:3px; min-width:0;">
            <span style="font-size:0.75rem; color:var(--mv-text-sub,#6c757d);">${k}</span>
            <span style="font-size:0.88rem; font-weight:500; color:var(--mv-text); overflow-wrap:anywhere;
                         ${mono ? 'font-family:ui-monospace,Menlo,monospace;' : ''}">${v ?? this._leeg()}</span>
          </div>`)}
      </div>`;
  }

  async _kopieer(tekst, sleutel) {
    try {
      await navigator.clipboard.writeText(tekst);
      this.copied = sleutel;
      setTimeout(() => { if (this.copied === sleutel) this.copied = null; }, 1400);
    } catch (_) { alertDialog({ title: 'Kopiëren mislukt', message: 'Kopiëren is niet gelukt.' }); }
  }

  _kopieerKnop(tekst, sleutel) {
    return html`<button @click=${() => this._kopieer(tekst, sleutel)}
      style="padding:5px 10px; border-radius:7px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff);
             font-weight:600; font-size:0.75rem; cursor:pointer; color:var(--mv-text);">
      ${this.copied === sleutel ? 'Gekopieerd' : 'Kopiëren'}</button>`;
  }

  _blokTitel(tekst) {
    return html`<span style="font-size:0.72rem; font-weight:700; color:var(--mv-text-sub,#6c757d); letter-spacing:0.04em;">${tekst}</span>`;
  }

  /** Eén regel in een pogingenlijst. */
  _pogingRij(kleur, nummer, meta, uitkomst) {
    return html`
      <div style="display:grid; grid-template-columns:12px 90px minmax(0,200px) minmax(0,1fr); gap:10px; align-items:center;
                  font-size:0.82rem; padding:8px 12px; border:1px solid var(--mv-border,#e9ecef); border-radius:8px;">
        <span style="width:8px; height:8px; border-radius:50%; background:${kleur};"></span>
        <span style="font-weight:600; color:var(--mv-text);">${nummer}</span>
        <span style="color:var(--mv-text-sub,#6c757d); font-family:ui-monospace,Menlo,monospace; font-size:0.76rem;">${meta}</span>
        <span style="color:var(--mv-text); overflow-wrap:anywhere;">${uitkomst}</span>
      </div>`;
  }

  _pre(tekst, opts = {}) {
    return html`<pre style="margin:0; padding:12px 14px; background:var(--mv-bg,#f4f6f8); border-radius:8px;
      white-space:pre-wrap; word-break:break-word; font-size:0.78rem; line-height:1.6; color:var(--mv-text);
      max-height:${opts.max || '320px'}; overflow:auto;">${tekst}</pre>`;
  }

  // ── De vier stappen ──────────────────────────────────────────────────────

  /** De totalen onderaan de stappenlijst. */
  _totalen(d) {
    const meta = d.run_meta;
    const tm = d.transcript_meta || meta?.transcriptie || null;
    const ex = meta?.extractie || null;

    // Van het begin van de transcriptie tot het wegschrijven. Maar bij een herverwerking is de
    // transcriptie de oude, van de eerste run: dan zou de tijd tussen beide runs meetellen (run
    // #270: 32,7 s voor 3 s werk). Begon de transcriptie vóór deze run, dan tellen we vanaf de run.
    const runStart = parseTimestamp(d.run?.started_at);
    const transcriptieStart = meta?.tijden?.transcribeStarted ? new Date(meta.tijden.transcribeStarted) : null;
    const hergebruikt = !!(runStart && transcriptieStart && transcriptieStart < runStart - 2000);
    const vanaf = hergebruikt ? runStart : transcriptieStart;
    const totaalMs = vanaf && meta?.tijden?.persistFinished ? new Date(meta.tijden.persistFinished) - vanaf : null;
    // Wat vóór de transcriptie gebeurt, en de hele keten vanaf stop op het toestel. Alleen bij een
    // eerste verwerking: bij een herverwerking ligt de upload in het verleden.
    const t = meta?.tijden || {};
    const ms = (a, b) => (a && b ? new Date(b) - new Date(a) : null);
    const voorafMs = !hergebruikt ? ms(t.uploadReceived, t.transcribeStarted) : null;
    const wachtrijMs = !hergebruikt ? ms(t.queued, t.pickedUp) : null;
    const stopTotKlaarMs = !hergebruikt && d.recording?.recorded_at ? ms(d.recording.recorded_at, t.persistFinished) : null;
    const tokens = (ex?.usage?.total_tokens ?? null) != null || (tm?.usage?.total_tokens ?? null) != null
      ? (ex?.usage?.total_tokens ?? 0) + (tm?.usage?.total_tokens ?? 0)
      : null;
    const kosten = meta?.kosten || null;

    const regel = (label, waarde, titel) => html`
      <div style="display:flex; justify-content:space-between; gap:10px; font-size:0.8rem;" title=${titel || ''}>
        <span style="color:var(--mv-text-sub,#6c757d);">${label}</span>
        <strong style="color:var(--mv-text); font-variant-numeric:tabular-nums;">${waarde ?? this._leeg()}</strong>
      </div>`;

    return html`
      <div style="margin-top:auto; padding:14px 10px 4px; border-top:1px solid var(--mv-border,#e9ecef);
                  display:flex; flex-direction:column; gap:10px;">
        ${regel(hergebruikt ? 'Totaal (alleen extractie)' : 'Totaal', fmtMs(totaalMs) ?? (d.timeline.sec_total != null ? `${d.timeline.sec_total}s` : null),
          hergebruikt
            ? 'Herverwerking: het transcript van de eerste run is hergebruikt; dit is de tijd van deze run tot het wegschrijven'
            : 'Van het begin van de transcriptie tot het wegschrijven')}
        ${voorafMs != null ? regel('Upload tot transcriptie', fmtMs(voorafMs),
          `Van het binnenkomen van de upload tot het begin van de transcriptie${wachtrijMs != null ? `; waarvan wachten op de verwerker ${fmtMs(wachtrijMs)}` : ''}`) : ''}
        ${!hergebruikt && t.via ? regel('Verwerkt via', `${{ 'eu-do': 'EU-verwerker', wachtrij: 'Wachtrij', 'wachtrij-vangnet': 'Wachtrij (vangnet)' }[t.via] || t.via}${t.verwerkerColo ? ` · ${t.verwerkerColo}` : ''}`,
          t.jurisdictie === 'eu' ? 'Gegarandeerd binnen de EU uitgevoerd (Durable Object met jurisdictie eu)' : 'Geen garantie over de plek van uitvoering; de code is het Cloudflare-datacenter') : ''}
        ${stopTotKlaarMs != null ? regel('Stop tot klaar (toestel)', fmtMs(stopTotKlaarMs),
          'Van stop op het toestel tot het wegschrijven. Volgt de klok van het toestel, dus bij benadering; het ophalen door de app komt er nog bij.') : ''}
        ${regel('Tokens', fmtGetal(tokens), 'Gemeten: het verbruik dat de API zelf teruggaf')}
        ${regel('Kosten (schatting)', fmtGeld(kosten?.totaal),
          kosten ? `Prijslijst van ${kosten.prijzenVan}${kosten.volledig ? '' : ' — onvolledig: niet elke stap is te beprijzen'}` : '')}
      </div>`;
  }

  // ── De panelen ───────────────────────────────────────────────────────────

  _paneelKop(titel, rechts) {
    return html`
      <div style="display:flex; justify-content:space-between; align-items:baseline; gap:12px; flex-wrap:wrap;">
        <h3 style="margin:0; font-size:1rem; color:var(--mv-text);">${titel}</h3>
        <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">${rechts || ''}</span>
      </div>`;
  }

  _paneelAudio(d) {
    const r = d.recording;
    const tm = d.transcript_meta || d.run_meta?.transcriptie || null;
    return html`
      <div style="display:flex; flex-direction:column; gap:16px;">
        ${this._paneelKop('1 · Audio', this.fmtDate(d.timeline.created_at))}
        <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding:12px 14px;
                    border:1px solid var(--mv-border,#e9ecef); border-radius:10px;">
          ${this.audioUrl
            ? html`<audio controls src=${this.audioUrl} style="height:34px; flex:1; min-width:240px;"></audio>`
            : html`<span style="flex:1; font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">${r.deleted
              ? 'Deze opname is verwijderd, bijvoorbeeld samen met zijn project. De audio en het transcript bestaan niet meer; wat de verwerking zelf bewaarde (berichten, uitvoer, tijden, kosten) staat hieronder.'
              : 'Geen audio beschikbaar.'}</span>`}
          ${r.deleted ? '' : html`<button @click=${this.downloadAudio}
            style="display:flex; align-items:center; gap:6px; padding:6px 12px; border-radius:8px; border:1px solid var(--mv-border);
                   background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:0.82rem; cursor:pointer;">
            ${unsafeSVG(lucideIcon(Download, 15))} Download
          </button>`}
        </div>
        ${this._kvGrid([
          ['Bestand', r.filename],
          ['Duur', fmtDuur(tm?.usage?.seconds) ?? this._leeg('Alleen vastgelegd als het transcriptiemodel de duur teruggaf')],
          ['Grootte', fmtBytes(d.audio?.bytes)],
          ['Geüpload', this.fmtDate(d.timeline.created_at)],
          ['Door', r.uploaded_by_email],
          ['Locatie', r.x != null && r.y != null ? `${r.x}, ${r.y}` : this._leeg('Geen GPS bij deze opname')],
        ], 3)}
      </div>`;
  }

  /**
   * Wat de spraakcontrole mat: hoeveel seconden van de opname een stem bevatten. Zonder spraak
   * wordt er niet getranscribeerd, want dan verzint het model een tekst. Zie
   * TranscriptionService.meetSpraak.
   */
  _spraakcontrole(tm) {
    const sc = tm?.spraakcontrole;
    if (!tm) return null;
    if (!sc) return 'uit';
    if (sc.fout) return `aan, niet gemeten (${sc.fout})`;
    const tekst = `aan · ${sc.spraak.toFixed(1)} s spraak van ${sc.duur.toFixed(1)} s`
      + (sc.woordenPerS != null ? ` · ${sc.woordenPerS.toFixed(1)} woorden/s` : '');
    const rood = (t) => html`<span style="color:var(--mv-danger,#dc3545); font-weight:600;">${t}</span>`;
    if (sc.geenSpraak) return rood(`${tekst}: geen spraak, niet getranscribeerd`);
    if (sc.teVeelTekst) return rood(`${tekst}: meer tekst dan er gesproken is (max ${sc.maxWoordenPerS}), afgekeurd`);
    return tekst;
  }

  /**
   * Waarom de hallucinatie preventie deze opname tegenhield, in één zin met de meting erin.
   * De getallen komen uit het logboek (transcript_meta.spraakcontrole); oudere meldingen zonder
   * logboek vallen terug op de tekst van de melding.
   */
  _blokPreventie(d, tm) {
    const p = hallucinatiePreventie(d.run?.error_message) || hallucinatiePreventie(d.recording?.error_message);
    if (!p) return '';
    const sc = tm?.spraakcontrole || {};
    const g = (n) => (typeof n === 'number' ? n.toFixed(1).replace('.', ',') : '?');
    const waarom = {
      'no-speech': `Er werd maar ${g(sc.spraak)} s gesproken in ${g(sc.duur)} s audio. Onder de ${sc.grens ?? 2} s maken we geen transcript.`,
      'too-much-text': `Het transcript heeft ${sc.woorden ?? '?'} woorden, maar er werd maar ${g(sc.spraak)} s gesproken: ${g(sc.woordenPerS)} woorden per seconde. Een mens haalt er hooguit ${sc.maxWoordenPerS ?? 5}.`,
      'vocabulary-echo': 'Het transcript is letterlijk de voorbeeldtekst uit de woordenlijst: er is niets gezegd.',
      'stock-phrase': 'Het transcript is een standaardzin die het model verzint als er niets gezegd wordt.',
    }[p.reden] || p.detail;
    return html`
      <div style="padding:10px 14px; border-radius:10px; background:rgba(245,158,11,0.1);
                  border:1px solid rgba(245,158,11,0.35); display:flex; flex-direction:column; gap:3px;">
        <span style="font-weight:700; font-size:0.85rem; color:#92400e;">🛡 Niet verwerkt door hallucinatie preventie</span>
        <span style="font-size:0.82rem; color:var(--mv-text);">${waarom}</span>
      </div>`;
  }

  _paneelTranscriptie(d) {
    const tm = d.transcript_meta || d.run_meta?.transcriptie || null;
    const r = d.recording;
    const kosten = d.run_meta?.kosten?.transcriptie ?? null;
    const temps = tm?.pogingen?.map((p) => p.temperature).filter((t) => t != null) ?? [];
    const whisper = (tm?.model || r.transcript_model) === 'whisper-1';
    const vocab = tm?.woordenlijst || null;

    return html`
      <div style="display:flex; flex-direction:column; gap:18px;">
        ${this._paneelKop('2 · Transcriptie', [
          this.fmtDate(d.timeline.transcribed_at),
          d.timeline.sec_to_transcribe != null ? `${d.timeline.sec_to_transcribe}s na upload` : null,
        ].filter(Boolean).join(' · '))}

        ${this._kvGrid([
          ['Model', tm?.model || r.transcript_model],
          ['Taal', tm
            ? (tm.gehoordeTaal || tm.taalMeegestuurd || this._leeg('Dit model geeft de gehoorde taal niet terug'))
            : (r.transcript_language || this._leeg()), true],
          ['Temperature', temps.length ? temps.map((t) => t.toFixed(1)).join(' → ') : null, true],
          ['Kosten', fmtGeld(kosten)],
          ['Hallucinatie preventie', this._spraakcontrole(tm)],
        ])}

        ${this._blokPreventie(d, tm)}

        ${this.transcriptKwaliteit(r)}

        ${vocab ? html`
          <div style="border:1px solid var(--mv-border,#e9ecef); border-radius:10px; overflow:hidden;">
            <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap;
                        padding:10px 14px; background:var(--mv-bg,#f8f9fa); border-bottom:1px solid var(--mv-border,#e9ecef);">
              <div style="display:flex; flex-direction:column; gap:2px; min-width:0;">
                <span style="font-weight:600; font-size:0.88rem; color:var(--mv-text);">Woordenlijst (vocabulaire)</span>
                <span style="font-size:0.75rem; color:var(--mv-text-sub,#6c757d);">
                  ${[vocab.titel ? `Transcriptieprompt "${vocab.titel}"` : 'Transcriptieprompt',
                     vocab.tokens != null ? `± ${fmtGetal(vocab.tokens)} tokens (schatting)` : null].filter(Boolean).join(' · ')}
                </span>
              </div>
              <div style="display:flex; gap:8px;">
                ${this._kopieerKnop(vocab.tekst, 'vocab')}
                <button @click=${() => { this.vocabOpen = !this.vocabOpen; }}
                  style="padding:5px 10px; border-radius:7px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff);
                         font-weight:600; font-size:0.75rem; cursor:pointer; color:var(--mv-text);">
                  ${this.vocabOpen ? 'Inklappen' : 'Volledig tonen'}</button>
              </div>
            </div>
            ${whisper ? html`
              <div style="padding:8px 14px; background:rgba(245,158,11,0.1); color:#92400e; font-size:0.76rem;
                          border-bottom:1px solid var(--mv-border,#e9ecef);">
                whisper-1 leest maximaal 224 tokens van deze lijst; wat daarboven staat wordt genegeerd.
              </div>` : ''}
            <pre style="margin:0; padding:12px 14px; white-space:pre-wrap; font-size:0.76rem; line-height:1.55;
                        color:var(--mv-text); max-height:${this.vocabOpen ? 'none' : '96px'}; overflow:hidden;">${vocab.tekst}</pre>
          </div>`
        : html`
          <div style="padding:10px 14px; border:1px dashed var(--mv-border,#e9ecef); border-radius:10px;
                      font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
            Geen woordenlijst meegestuurd bij deze transcriptie.
          </div>`}

        ${tm?.pogingen?.length ? html`
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${this._blokTitel('POGINGEN')}
            ${tm.pogingen.map((p) => this._pogingRij(
              p.uitkomst === 'geaccepteerd' ? '#22a35a' : p.uitkomst === 'herhalingslus' ? '#f59e0b' : 'var(--mv-danger,#dc3545)',
              `Poging ${p.n}`,
              [p.planB ? 'plan B' : null,
               p.temperature != null ? `temperature ${p.temperature.toFixed(1)}` : null,
               p.model,
               fmtMs(p.ms)].filter(Boolean).join(' · '),
              p.uitkomst === 'geaccepteerd' ? 'Geaccepteerd'
                : p.uitkomst === 'herhalingslus' && p.lus ? `Herhalingslus gedetecteerd (${p.lus.aantal}× "${p.lus.tekst}"), opnieuw`
                : p.reden || p.uitkomst || '—',
            ))}
            ${tm.pogingen.some((p) => p.herkansingen > 0) ? html`
              <span style="font-size:0.75rem; color:var(--mv-text-sub,#6c757d);">
                Herkansingen op 429/5xx zijn meegeteld in de duur per poging.
              </span>` : ''}
          </div>` : ''}

        <div style="display:flex; flex-direction:column; gap:8px;">
          ${this._blokTitel('TRANSCRIPT')}
          ${d.transcript ? this._pre(d.transcript) : html`<span style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">Nog geen transcript.</span>`}
        </div>
      </div>`;
  }

  _paneelExtractie(d) {
    const ex = d.run_meta?.extractie || null;
    const kosten = d.run_meta?.kosten?.extractie ?? null;
    const tabs = [
      ['prompt', 'Prompt'],
      ['json', 'Ruwe AI-output'],
      ['verwerkt', 'Na verwerking'],
      ['pogingen', `Pogingen${ex?.pogingen?.length ? ` (${ex.pogingen.length})` : ''}`],
    ];
    const tab = this.tab || 'prompt';

    return html`
      <div style="display:flex; flex-direction:column; gap:18px;">
        ${this._paneelKop(`3 · Extractie — ${getExtractionProfile(this._profileOf(d.recording)).label}`, [
          this.fmtDate(d.timeline.processed_at),
          d.timeline.sec_transcribe_to_ai != null ? `${d.timeline.sec_transcribe_to_ai}s na transcript` : null,
        ].filter(Boolean).join(' · '))}

        ${this._kvGrid([
          ['Model', ex?.model || d.ai_config?.extractionModel],
          ['Temperature', ex?.temperature != null ? ex.temperature.toFixed(1) : null, true],
          ['Response format', ex ? [ex.responseFormat, ex.schemaStrict ? '(strict)' : null].filter(Boolean).join(' ') : null, true],
          ['Prompt', d.run?.prompt_title || d.ai_config?.promptTitle || (d.ai_config?.promptVersionId ? 'aangepast' : null)],
          ['Tokens in', fmtGetal(ex?.usage?.prompt_tokens)],
          ['Tokens uit', fmtGetal(ex?.usage?.completion_tokens)],
          ['Kosten', fmtGeld(kosten) ? `${fmtGeld(kosten)}${ex?.serviceTier === 'priority' ? ' (priority-tarief)' : ''}` : null],
          ['Pogingen', ex?.pogingen?.length ? String(ex.pogingen.length) : null],
          // Snelheid: compacte uitvoer (en of die terug moest vallen) en OpenAI priority.
          ['Snelle uitvoer', ex?.compact === undefined ? null
            : !ex.compact ? 'uit'
            : ex.compactTerugval ? html`<span style="color:#92400e;" title=${ex.compactTerugval}>aan, teruggevallen op volledig</span>`
            : 'aan'],
          ['Priority', ex?.serviceTierGevraagd === undefined ? null
            : !ex.serviceTierGevraagd ? 'uit'
            : ex.priorityGeweigerd ? html`<span style="color:#92400e;" title=${ex.priorityGeweigerd}>gevraagd, geweigerd</span>`
            : `aan (${ex.serviceTier || '?'})`],
        ])}

        <div style="display:flex; gap:4px; border-bottom:1px solid var(--mv-border,#e9ecef);">
          ${tabs.map(([id, label]) => html`
            <button @click=${() => { this.tab = id; }}
              style="padding:8px 14px; border:none; background:none; margin-bottom:-1px; cursor:pointer;
                     font-weight:600; font-size:0.82rem;
                     border-bottom:2px solid ${tab === id ? 'var(--mv-primary,#1f4d2b)' : 'transparent'};
                     color:${tab === id ? 'var(--mv-text)' : 'var(--mv-text-sub,#6c757d)'};">${label}</button>`)}
        </div>

        ${tab === 'prompt' ? this._tabPrompt(ex) : ''}
        ${tab === 'json' ? this._tabRuweUitvoer(ex) : ''}
        ${tab === 'verwerkt' ? this._tabNaVerwerking(d, ex) : ''}
        ${tab === 'pogingen' ? this._tabPogingen(ex) : ''}
      </div>`;
  }

  /** Letterlijk wat het model terugschreef, vóór aanvullen en correcties. */
  _tabRuweUitvoer(ex) {
    const leeg = (t) => html`<span style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">${t}</span>`;
    if (!ex?.ruweUitvoer) return leeg('De letterlijke uitvoer van het model is bij deze verwerking niet vastgelegd (runs van vóór 13 oktober). Zie "Na verwerking".');
    const mooi = (t) => { try { return JSON.stringify(JSON.parse(t), null, 2); } catch (_) { return t; } };
    return html`
      <div style="display:flex; flex-direction:column; gap:10px;">
        ${ex.compact ? html`<span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">
          Snelle uitvoer stond aan: het model schreef alleen de velden die het herkende. De software vult de rest aan; zie "Na verwerking".</span>` : ''}
        ${ex.ruweUitvoerCompact ? html`
          <span style="font-size:0.8rem; color:#92400e;">Deze compacte uitvoer paste niet zonder verlies (${ex.compactTerugval}); daarom is het daarna nog één keer volledig gevraagd.</span>
          ${this._pre(mooi(ex.ruweUitvoerCompact), { max: '260px' })}
          <span style="font-size:0.8rem; font-weight:600; color:var(--mv-text);">De volledige tweede uitvoer:</span>` : ''}
        ${this._pre(mooi(ex.ruweUitvoer), { max: '420px' })}
      </div>`;
  }

  /** Wat we van de uitvoer maakten: aangevuld tot het volledige formaat, en de vaste correcties. */
  _tabNaVerwerking(d, ex) {
    if (!d.result_data) return html`<span style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">Geen verwerkte uitvoer beschikbaar.</span>`;
    const lijst = (titel, items) => items?.length ? html`
      <div style="display:flex; flex-direction:column; gap:4px;">
        <span style="font-size:0.8rem; font-weight:600; color:var(--mv-text);">${titel} (${items.length})</span>
        ${items.map((t) => html`<span style="font-size:0.76rem; font-family:ui-monospace,Menlo,monospace; color:var(--mv-text-sub,#4b5563);">${typeof t === 'string' ? t : JSON.stringify(t)}</span>`)}
      </div>` : '';
    return html`
      <div style="display:flex; flex-direction:column; gap:12px;">
        <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">
          Zoals het boorprofiel is opgeslagen: lege velden aangevuld tot het volledige formaat${ex?.compact ? '' : ' (bij volledige uitvoer schreef het model die zelf)'}, plus de vaste correcties.</span>
        ${lijst('Hersteld (verplaatst of tikfout)', ex?.compactVerplaatst)}
        ${lijst('Afgeleid aangevuld', ex?.compactAangevuld)}
        ${lijst('Correcties', Array.isArray(ex?.correcties) ? ex.correcties : null)}
        ${this._pre(typeof d.result_data === 'string' ? d.result_data : JSON.stringify(d.result_data, null, 2), { max: '420px' })}
      </div>`;
  }

  /** De exact verstuurde berichten. Zonder die tekst is een rare uitkomst niet te verklaren. */
  _tabPrompt(ex) {
    if (!ex?.messages?.length) {
      return html`<div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">
        De verstuurde prompt is bij deze verwerking niet vastgelegd. Verwerk opnieuw om hem vast te leggen.
      </div>`;
    }
    const fewShot = ex.messages.filter((m) => m.fewShot).length;
    return html`
      <div style="display:flex; flex-direction:column; gap:12px;">
        ${ex.messages.map((m, i) => html`
          <div style="border:1px solid var(--mv-border,#e9ecef); border-radius:10px; overflow:hidden;">
            <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap;
                        padding:8px 14px; background:var(--mv-bg,#f8f9fa); border-bottom:1px solid var(--mv-border,#e9ecef);">
              <div style="display:flex; gap:10px; align-items:baseline; min-width:0;">
                <span style="font-weight:700; font-size:0.8rem; font-family:ui-monospace,Menlo,monospace; color:var(--mv-text);">${m.role}</span>
                <span style="font-size:0.75rem; color:var(--mv-text-sub,#6c757d);">
                  ${[m.fewShot ? 'voorbeeld (few-shot)' : null,
                     m.tokens != null ? `± ${fmtGetal(m.tokens)} tokens (schatting)` : null].filter(Boolean).join(' · ')}
                </span>
              </div>
              ${this._kopieerKnop(m.content, `msg${i}`)}
            </div>
            ${this._pre(m.content, { max: '320px' })}
          </div>`)}
        <div style="font-size:0.75rem; color:var(--mv-text-sub,#6c757d);">
          ${[ex.responseFormat ? html`Response format: <code>${ex.responseFormat}</code>` : null,
             ex.schemaNaam ? html` · <code>${ex.schemaNaam}</code>` : null,
             ex.schemaStrict ? ' · strict' : null]}
          ${fewShot ? html`<br>${fewShot} van deze berichten zijn voorbeelden uit de promptversie.` : ''}
          <br>De tokenaantallen per bericht zijn geschat; <strong>Tokens in</strong> hierboven is gemeten en telt
          ook het JSON-schema mee, dat niet in deze berichten staat.
        </div>
      </div>`;
  }

  _tabPogingen(ex) {
    if (!ex?.pogingen?.length) {
      return html`<div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">
        De pogingen zijn bij deze verwerking niet vastgelegd.
      </div>`;
    }
    return html`
      <div style="display:flex; flex-direction:column; gap:8px;">
        ${ex.pogingen.map((p) => this._pogingRij(
          p.uitkomst === 'gelukt' ? '#22a35a' : p.uitkomst === 'opnieuw' ? '#f59e0b' : 'var(--mv-danger,#dc3545)',
          `Poging ${p.n}`,
          [p.status != null ? `${p.status}` : 'geen antwoord', fmtMs(p.ms)].filter(Boolean).join(' · '),
          p.uitkomst === 'gelukt' ? 'Geldige JSON volgens het schema'
            : p.uitkomst === 'opnieuw' ? `Opnieuw geprobeerd na ${fmtMs(p.wachtMs)} wachten`
            : p.uitkomst === 'time-out' ? `Afgebroken: ${p.reden || 'geen antwoord'}`
            : p.uitkomst === 'netwerkfout' ? `Geen verbinding: ${p.reden || ''}`
            : `Opgegeven${p.reden ? `: ${p.reden}` : ''}`,
        ))}
        ${ex.fout ? html`<div style="font-size:0.8rem; color:var(--mv-danger,#dc3545);">${ex.fout}</div>` : ''}
      </div>`;
  }

  _paneelUitkomst(d) {
    const dr = d.domain_result;
    const boringLink = dr?.boringId || d.borings?.[0]?.id;
    return html`
      <div style="display:flex; flex-direction:column; gap:16px;">
        ${this._paneelKop('4 · Uitkomst', boringLink && d.recording.project_id
          ? html`<a href=${`/projects/${d.recording.project_id}`} style="font-weight:600;">Open boring in project</a>`
          : '')}
        ${dr ? html`
          ${dr.boringName ? html`<div style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
            Geschreven naar boring <strong style="color:var(--mv-text);">${dr.boringName}</strong></div>` : ''}
          ${dr.domain === 'peilbuis' ? this._renderPeilbuizen(dr.peilbuizen)
            : dr.domain === 'watermonster' ? this._renderWatermonsters(dr.watermonsters)
            : this._renderAfwerking(dr.afwerking)}`
        : d.borings?.length ? html`
          ${d.borings.map((b) => html`
            <div style="display:flex; flex-direction:column; gap:8px;">
              <div style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
                Geschreven naar boring <strong style="color:var(--mv-text);">${b.name || b.id}</strong> · ${b.layer_count} lagen
              </div>
              ${b.layers?.length ? html`
                <table style="width:100%; border-collapse:collapse; font-size:0.82rem;">
                  <thead><tr style="text-align:left; color:var(--mv-text-sub,#6c757d); font-size:0.76rem;">
                    <th style="padding:8px 10px; font-weight:600; border-bottom:1px solid var(--mv-border,#e9ecef);">Diepte</th>
                    <th style="padding:8px 10px; font-weight:600; border-bottom:1px solid var(--mv-border,#e9ecef);">Grondsoort</th>
                    <th style="padding:8px 10px; font-weight:600; border-bottom:1px solid var(--mv-border,#e9ecef);">Kleur</th>
                    <th style="padding:8px 10px; font-weight:600; border-bottom:1px solid var(--mv-border,#e9ecef);">BZB</th>
                  </tr></thead>
                  <tbody>
                    ${b.layers.map((l) => html`
                      <tr>
                        <td style="padding:9px 10px; border-bottom:1px solid var(--mv-bg,#f1f3f5); white-space:nowrap; font-variant-numeric:tabular-nums; color:var(--mv-text);">${l.upper_depth ?? '?'}–${l.lower_depth ?? '?'} cm</td>
                        <td style="padding:9px 10px; border-bottom:1px solid var(--mv-bg,#f1f3f5); color:var(--mv-text);">${this._soilText(l)}</td>
                        <td style="padding:9px 10px; border-bottom:1px solid var(--mv-bg,#f1f3f5); color:var(--mv-text-sub,#6c757d);">${this._kleurText(l) || '—'}</td>
                        <td style="padding:9px 10px; border-bottom:1px solid var(--mv-bg,#f1f3f5); color:var(--mv-text-sub,#6c757d);">${this._bzbText(l) || '—'}</td>
                      </tr>`)}
                  </tbody>
                </table>` : html`<span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">Geen lagen.</span>`}
            </div>`)}`
        : html`<div style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">
            ${d.run && !d.run.isLatest
              ? 'De geparste tabel toont alleen de laatste verwerking. Voor deze oudere run staat de ruwe AI-output bij Extractie.'
              : 'Niets weggeschreven.'}
          </div>`}
      </div>`;
  }

  /**
   * Het detail van één run: links de vier stappen, rechts het paneel van de gekozen stap.
   *
   * De stap die opengaat is niet willekeurig. Bij een fout is dat de stap die faalde -- daar kom
   * je voor -- en anders de transcriptie, want dat is de stap waar de meeste vragen over gaan.
   */
  renderDetail() {
    const d = this.selected;
    if (d?._loading || this.detailLoading) {
      return html`<div style="padding:32px; text-align:center; color:var(--mv-text-sub,#6c757d);">Laden…</div>`;
    }
    const lijst = stappen(d);
    const actief = this.step && lijst.some((s) => s.id === this.step) ? this.step : standaardStap(lijst);

    return html`
      <div style="flex:1; display:grid; grid-template-columns:290px minmax(0,1fr); min-height:0;">
        <div style="background:var(--mv-bg,#f8f9fa); border-right:1px solid var(--mv-border,#e9ecef);
                    padding:18px 14px; display:flex; flex-direction:column; gap:4px; overflow-y:auto;">
          ${lijst.map((s) => {
            const aan = s.id === actief;
            const [dotBg, dotFg] = stapKleuren(s.staat);
            return html`
              <button @click=${() => { this.step = s.id; }}
                title=${s.staat === 'onbekend' ? 'Van deze stap is bij deze verwerking niets vastgelegd' : ''}
                style="display:grid; grid-template-columns:28px minmax(0,1fr) auto; gap:10px; align-items:start; text-align:left;
                       padding:10px; border-radius:10px; cursor:pointer;
                       border:1px solid ${aan ? 'var(--mv-border,#dee2e6)' : 'transparent'};
                       background:${aan ? 'var(--mv-surface,#fff)' : 'transparent'};">
                <span style="width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center;
                             font-size:0.75rem; font-weight:700; background:${dotBg}; color:${dotFg};">${s.n}</span>
                <span style="display:flex; flex-direction:column; gap:2px; min-width:0;">
                  <span style="font-weight:600; font-size:0.88rem; color:var(--mv-text);">${s.titel}</span>
                  <span style="font-size:0.76rem; color:var(--mv-text-sub,#6c757d); overflow-wrap:anywhere;">${s.sub || '—'}</span>
                  ${s.notitie ? html`<span style="font-size:0.76rem; font-weight:600; color:${s.notitieKleur};">${s.notitie}</span>` : ''}
                </span>
                <span style="font-size:0.76rem; font-weight:600; color:var(--mv-text-sub,#4b5563); font-variant-numeric:tabular-nums;">${s.duur || ''}</span>
              </button>`;
          })}
          ${this._totalen(d)}
        </div>

        <div style="overflow-y:auto; padding:22px 26px 28px;">
          ${actief === 'audio' ? this._paneelAudio(d) : ''}
          ${actief === 'transcriptie' ? this._paneelTranscriptie(d) : ''}
          ${actief === 'extractie' ? this._paneelExtractie(d) : ''}
          ${actief === 'uitkomst' ? this._paneelUitkomst(d) : ''}
        </div>
      </div>`;
  }

  // ── AI-aanroepen buiten de opnamepijplijn ─────────────────────────────────
  //
  // De mengmonsterassistent en het inlezen van een veldwerkopdracht (backend: AiCallRepository).
  // Geen audio en geen transcriptie: een aanroep is invoer → prompt → antwoord → uitkomst. Zelfde
  // popup, zelfde stappenlijst links, andere stappen.

  static AI_SOORTEN = {
    mengmonsters: {
      label: 'Mengmonsters', type: 'Vraag aan Flip', kleur: ['rgba(124,58,237,0.12)', '#6d28d9'],
      uitleg: 'De mengmonsterassistent op een labopdracht: een vraag van de adviseur wordt een filter, een voorstel of een antwoord. Het voorstel zelf rekent de backoffice uit met de mengregels.',
    },
    veldwerkopdracht: {
      label: 'Veldwerkopdracht', type: 'Inlezen', kleur: ['rgba(219,39,119,0.12)', '#be185d'],
      uitleg: 'Een veldwerkopdracht (tekst uit PDF, Word of Excel) omgezet naar de gestructureerde opdracht in GeoVoice.',
    },
    'veldwerkopdracht-ocr': {
      label: 'Veldwerkopdracht (scan)', type: 'Scan lezen', kleur: ['rgba(219,39,119,0.08)', '#9d174d'],
      uitleg: 'Een gescande veldwerkopdracht: het model neemt de tekst van de pagina\'s letterlijk over. Die tekst gaat daarna door "Veldwerkopdracht".',
    },
  };

  _aiSoort(kind) {
    return AnalysisPage.AI_SOORTEN[kind] || { label: kind || 'AI', type: 'AI', kleur: ['var(--mv-bg,#f1f3f5)', 'var(--mv-text-sub,#6c757d)'], uitleg: '' };
  }

  _aiBadge(kind) {
    const s = this._aiSoort(kind);
    return html`<span title=${s.uitleg}
      style="display:inline-flex; align-items:center; padding:2px 9px; border-radius:999px; font-size:0.75rem; font-weight:700; background:${s.kleur[0]}; color:${s.kleur[1]}; white-space:nowrap;">${s.label}</span>`;
  }

  /** De stappen links, met de staat van elke stap. */
  _aiStappen(c) {
    const fout = c.status === 'error';
    const heeftAntwoord = !!c.raw_output;
    const tokens = (a, b) => (a == null && b == null ? null : `${fmtGetal(a ?? 0)} in · ${fmtGetal(b ?? 0)} uit`);
    return [
      { id: 'invoer', n: 1, titel: 'Invoer', staat: 'ok',
        sub: c.kind === 'mengmonsters' ? (c.question || '—') : (c.filename || 'zonder bestandsnaam') },
      { id: 'prompt', n: 2, titel: 'Prompt', staat: c.messages?.length ? 'ok' : 'onbekend',
        sub: c.messages?.length ? `${c.messages.length} ${c.messages.length === 1 ? 'bericht' : 'berichten'} · ${c.model || '?'}` : 'niet vastgelegd' },
      { id: 'antwoord', n: 3, titel: 'Antwoord van het model', staat: heeftAntwoord ? 'ok' : (fout ? 'fout' : 'onbekend'),
        sub: heeftAntwoord ? (tokens(c.prompt_tokens, c.completion_tokens) || 'ontvangen') : (fout ? 'geen antwoord' : '—'),
        duur: fmtMs(c.ms) },
      { id: 'uitkomst', n: 4, titel: 'Uitkomst', staat: fout ? 'fout' : (c.result ? 'ok' : 'onbekend'),
        sub: fout ? 'mislukt' : c.kind === 'mengmonsters' ? (c.action ? `actie: ${c.action}` : '—') : 'opgeslagen' },
    ];
  }

  renderAiKop() {
    const d = this.selected;
    const c = d.call;
    const s = this._aiSoort(c.kind);
    return html`
      <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:16px;
                  padding:20px 24px 16px; border-bottom:1px solid var(--mv-border,#e9ecef);">
        <div style="display:flex; flex-direction:column; gap:8px; min-width:0;">
          <div style="display:flex; align-items:baseline; gap:12px; flex-wrap:wrap;">
            <h2 style="margin:0; font-size:1.12rem; font-weight:700; color:var(--mv-text);">AI-aanroep</h2>
            <span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
              ${[d.company?.name, c.project_name || c.project_id, c.lab_order_number, c.user_email, this.fmtDate(c.started_at)].filter(Boolean).join(' · ')}
            </span>
          </div>
          <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
            ${this.statusBadge(c.status, false)}
            ${this._aiBadge(c.kind)}
            <span style="display:inline-flex; align-items:center; padding:3px 10px; border-radius:999px; font-size:0.76rem;
                         font-weight:700; background:var(--mv-bg,#f1f3f5); color:var(--mv-text-sub,#6c757d);">${s.type}</span>
            <span style="font-size:0.76rem; color:var(--mv-text-sub,#6c757d); font-family:ui-monospace,Menlo,monospace;">aanroep #${c.id}</span>
          </div>
        </div>
        <button @click=${this.closeDetail}
          style="width:34px; height:34px; border:none; background:none; font-size:1.5rem; line-height:1;
                 color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
      </div>
      ${c.status === 'error' ? html`
        <div style="margin:12px 24px 0; padding:10px 14px; border-radius:8px; background:rgba(220,53,69,0.08);
                    border:1px solid rgba(220,53,69,0.25); display:flex; flex-direction:column; gap:2px;">
          <span style="font-weight:700; font-size:0.85rem; color:var(--mv-danger,#dc3545);">Mislukt</span>
          <span style="font-size:0.78rem; color:var(--mv-text-sub,#4b5563); overflow-wrap:anywhere;">${c.error_message || 'Reden niet vastgelegd'}</span>
        </div>` : ''}`;
  }

  renderAiDetail() {
    const c = this.selected.call;
    const lijst = this._aiStappen(c);
    const actief = this.step && lijst.some((s) => s.id === this.step) ? this.step
      : (lijst.find((s) => s.staat === 'fout')?.id || 'invoer');
    const regel = (label, waarde, titel) => html`
      <div style="display:flex; justify-content:space-between; gap:10px; font-size:0.8rem;" title=${titel || ''}>
        <span style="color:var(--mv-text-sub,#6c757d);">${label}</span>
        <strong style="color:var(--mv-text); font-variant-numeric:tabular-nums;">${waarde ?? this._leeg()}</strong>
      </div>`;
    return html`
      <div style="flex:1; display:grid; grid-template-columns:290px minmax(0,1fr); min-height:0;">
        <div style="background:var(--mv-bg,#f8f9fa); border-right:1px solid var(--mv-border,#e9ecef);
                    padding:18px 14px; display:flex; flex-direction:column; gap:4px; overflow-y:auto;">
          ${lijst.map((s) => {
            const aan = s.id === actief;
            const [dotBg, dotFg] = stapKleuren(s.staat);
            return html`
              <button @click=${() => { this.step = s.id; }}
                style="display:grid; grid-template-columns:28px minmax(0,1fr) auto; gap:10px; align-items:start; text-align:left;
                       padding:10px; border-radius:10px; cursor:pointer;
                       border:1px solid ${aan ? 'var(--mv-border,#dee2e6)' : 'transparent'};
                       background:${aan ? 'var(--mv-surface,#fff)' : 'transparent'};">
                <span style="width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center;
                             font-size:0.75rem; font-weight:700; background:${dotBg}; color:${dotFg};">${s.n}</span>
                <span style="display:flex; flex-direction:column; gap:2px; min-width:0;">
                  <span style="font-weight:600; font-size:0.88rem; color:var(--mv-text);">${s.titel}</span>
                  <span style="font-size:0.76rem; color:var(--mv-text-sub,#6c757d); overflow-wrap:anywhere;
                               display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;">${s.sub || '—'}</span>
                </span>
                <span style="font-size:0.76rem; font-weight:600; color:var(--mv-text-sub,#4b5563); font-variant-numeric:tabular-nums;">${s.duur || ''}</span>
              </button>`;
          })}
          <div style="margin-top:auto; padding:14px 10px 4px; border-top:1px solid var(--mv-border,#e9ecef);
                      display:flex; flex-direction:column; gap:10px;">
            ${regel('Duur', fmtMs(c.ms), 'Van het versturen naar het model tot het antwoord, inclusief nieuwe pogingen')}
            ${regel('Tokens', fmtGetal((c.prompt_tokens != null || c.completion_tokens != null) ? (c.prompt_tokens ?? 0) + (c.completion_tokens ?? 0) : null),
              'Gemeten: het verbruik dat de API zelf teruggaf')}
            ${regel('Kosten (schatting)', fmtGeld(c.kosten), 'Zelfde prijslijst als de extractie in de pijplijn')}
          </div>
        </div>

        <div style="overflow-y:auto; padding:22px 26px 28px;">
          ${actief === 'invoer' ? this._aiInvoer(c) : ''}
          ${actief === 'prompt' ? this._aiPrompt(c) : ''}
          ${actief === 'antwoord' ? this._aiAntwoord(c) : ''}
          ${actief === 'uitkomst' ? this._aiUitkomst(c) : ''}
        </div>
      </div>`;
  }

  /** Wat de gebruiker aanleverde, en (bij de assistent) wat er verder in beeld was. */
  _aiInvoer(c) {
    const s = this._aiSoort(c.kind);
    const json = (v) => JSON.stringify(v, null, 2);
    const userBericht = [...(c.messages || [])].reverse().find((m) => m.role === 'user')?.content || '';
    if (c.kind === 'mengmonsters') {
      const ctx = c.request?.context || {};
      const gesprek = ctx.history || [];
      return html`
        <div style="display:flex; flex-direction:column; gap:18px;">
          ${this._paneelKop('1 · Invoer — vraag aan Flip', this.fmtDate(c.started_at))}
          <span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">${s.uitleg}</span>
          ${this._kvGrid([
            ['Gebruiker', c.user_email],
            ['Project', c.project_name || c.project_id],
            ['Labopdracht', c.lab_order_number || c.subject_id],
            ['Potjes in beeld', ctx.pots ? String(ctx.pots.length) : null],
          ])}
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${this._blokTitel('DE VRAAG')}
            ${this._pre(c.question || '—', { max: '160px' })}
          </div>
          ${gesprek.length ? html`
            <div style="display:flex; flex-direction:column; gap:8px;">
              ${this._blokTitel(`EERDER IN HET GESPREK (${gesprek.length})`)}
              ${this._pre(gesprek.map((m) => `${m.role === 'ai' ? 'Flip' : 'Adviseur'}: ${m.text}`).join('\n\n'), { max: '240px' })}
            </div>` : ''}
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${this._blokTitel('FILTER OP DAT MOMENT')}
            ${this._pre(json(c.request?.filter ?? null), { max: '200px' })}
          </div>
          <div style="display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              ${this._blokTitel('MEEGESTUURDE CONTEXT (potjes, mengmonsters, voorstel, keuzelijsten)')}
              ${c.request ? this._kopieerKnop(json(c.request), 'ai-context') : ''}
            </div>
            ${c.request ? this._pre(json({ context: c.request.context, options: c.request.options }), { max: '360px' }) : this._leeg()}
          </div>
        </div>`;
    }
    return html`
      <div style="display:flex; flex-direction:column; gap:18px;">
        ${this._paneelKop(`1 · Invoer — ${s.label.toLowerCase()}`, this.fmtDate(c.started_at))}
        <span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">${s.uitleg}</span>
        ${this._kvGrid([
          ['Gebruiker', c.user_email],
          ['Project', c.project_name || c.project_id],
          ['Bestand', c.filename],
          ['Model', c.model, true],
        ])}
        ${c.kind === 'veldwerkopdracht-ocr' ? this._scanNaastTekst(c) : html`
          <div style="display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              ${this._blokTitel('DE TEKST DIE ER INGING')}
              ${userBericht ? this._kopieerKnop(userBericht, 'ai-invoer') : ''}
            </div>
            ${userBericht ? this._pre(userBericht, { max: '460px' }) : this._leeg()}
          </div>`}
      </div>`;
  }

  /** Een scan: de pagina's links, de herkende tekst rechts. */
  _scanNaastTekst(c) {
    const tekst = c.result?.text || c.raw_output || '';
    const paginas = this.scanPaginas || [];
    return html`
      <div style="display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:16px; align-items:start;">
        <div style="display:flex; flex-direction:column; gap:8px; min-width:0;">
          ${this._blokTitel(`DE SCAN${c.pages ? ` (${c.pages} ${c.pages === 1 ? 'PAGINA' : "PAGINA'S"})` : ''}`)}
          ${!c.pages ? html`<span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
              De pagina's zijn bij deze aanroep niet bewaard (van vóór 29 oktober).</span>`
            : html`<div style="display:flex; flex-direction:column; gap:10px; max-height:560px; overflow:auto;">
                ${Array.from({ length: c.pages }, (_, i) => paginas[i]
                  ? html`<a href=${paginas[i]} target="_blank" rel="noopener" title="Pagina ${i + 1} groot openen">
                      <img src=${paginas[i]} alt="Pagina ${i + 1}" style="display:block; width:100%; border:1px solid var(--mv-border,#e9ecef); border-radius:8px;"></a>`
                  : html`<div style="padding:24px; text-align:center; font-size:0.8rem; color:var(--mv-text-sub,#6c757d);
                      border:1px dashed var(--mv-border,#e9ecef); border-radius:8px;">Pagina ${i + 1} laden…</div>`)}
              </div>`}
        </div>
        <div style="display:flex; flex-direction:column; gap:8px; min-width:0;">
          <div style="display:flex; justify-content:space-between; align-items:center;">
            ${this._blokTitel('HERKENDE TEKST')}
            ${tekst ? this._kopieerKnop(tekst, 'ai-invoer') : ''}
          </div>
          ${tekst ? this._pre(tekst, { max: '560px' }) : html`<span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
              Geen tekst herkend${c.error_message ? `: ${c.error_message}` : ''}.</span>`}
        </div>
      </div>`;
  }

  /** De berichten precies zoals ze naar het model gingen. */
  _aiPrompt(c) {
    return html`
      <div style="display:flex; flex-direction:column; gap:18px;">
        ${this._paneelKop('2 · Prompt', c.model || '')}
        ${this._kvGrid([
          ['Model', c.model, true],
          ['Berichten', c.messages?.length ? String(c.messages.length) : null],
          ['Tokens in', fmtGetal(c.prompt_tokens)],
          ['Tokens uit', fmtGetal(c.completion_tokens)],
        ])}
        ${this._tabPrompt({ messages: c.messages })}
      </div>`;
  }

  /** Letterlijk wat het model terugschreef. */
  _aiAntwoord(c) {
    const mooi = (t) => { try { return JSON.stringify(JSON.parse(t), null, 2); } catch (_) { return t; } };
    return html`
      <div style="display:flex; flex-direction:column; gap:18px;">
        ${this._paneelKop('3 · Antwoord van het model', fmtMs(c.ms) || '')}
        ${c.raw_output ? html`
          <div style="display:flex; flex-direction:column; gap:8px;">
            <div style="display:flex; justify-content:space-between; align-items:center;">
              ${this._blokTitel('RUWE AI-OUTPUT')}
              ${this._kopieerKnop(c.raw_output, 'ai-ruw')}
            </div>
            ${this._pre(mooi(c.raw_output), { max: '560px' })}
          </div>`
          : html`<span style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">
              Er kwam geen antwoord van het model${c.error_message ? `: ${c.error_message}` : ''}.</span>`}
      </div>`;
  }

  /** Wat de backend van het antwoord maakte. */
  _aiUitkomst(c) {
    const r = c.result;
    const leeg = html`<span style="font-size:0.85rem; color:var(--mv-text-sub,#6c757d);">
      ${c.status === 'error' ? `Geen uitkomst: ${c.error_message || 'de aanroep is mislukt'}.` : 'Geen uitkomst vastgelegd.'}</span>`;
    let inhoud = leeg;
    if (r && c.kind === 'mengmonsters') {
      const ACTIES = {
        filter: 'Filter gezet op het lagenoverzicht', propose: 'Mengmonsters voorstellen (de backoffice rekent het voorstel uit)',
        edit: 'Het mandje bewerkt', answer: 'Alleen een antwoord', undo: 'Laatste stap ongedaan gemaakt',
        clear: 'Filter gewist', check: 'Gecontroleerd of elke laag in een mengmonster zit',
      };
      inhoud = html`
        ${this._kvGrid([['Actie', r.action, true], ['Betekent', ACTIES[r.action] || '—']], 2)}
        ${r.answer ? html`
          <div style="display:flex; flex-direction:column; gap:8px;">
            ${this._blokTitel('WAT FLIP ZEI')}
            ${this._pre(r.answer, { max: '200px' })}
          </div>` : ''}
        <div style="display:flex; flex-direction:column; gap:8px;">
          ${this._blokTitel('NA CONTROLE DOOR DE BACKEND')}
          ${this._pre(JSON.stringify(r, null, 2), { max: '420px' })}
        </div>`;
    } else if (r && c.kind === 'veldwerkopdracht-ocr') {
      inhoud = html`
        <div style="display:flex; flex-direction:column; gap:8px;">
          <div style="display:flex; justify-content:space-between; align-items:center;">
            ${this._blokTitel('DE OVERGENOMEN TEKST (vakjes als [x] / [ ])')}
            ${this._kopieerKnop(r.text || '', 'ai-uitkomst')}
          </div>
          ${this._pre(r.text || '—', { max: '560px' })}
        </div>`;
    } else if (r) {
      inhoud = html`
        <span style="font-size:0.8rem; color:var(--mv-text-sub,#6c757d);">
          De opdracht zoals hij als concept is opgeslagen, na het normaliseren van het antwoord.</span>
        <div style="display:flex; flex-direction:column; gap:8px;">
          <div style="display:flex; justify-content:space-between; align-items:center;">
            ${this._blokTitel('GESTRUCTUREERDE VELDWERKOPDRACHT')}
            ${this._kopieerKnop(JSON.stringify(r, null, 2), 'ai-uitkomst')}
          </div>
          ${this._pre(JSON.stringify(r, null, 2), { max: '560px' })}
        </div>`;
    }
    return html`
      <div style="display:flex; flex-direction:column; gap:18px;">
        ${this._paneelKop('4 · Uitkomst', '')}
        ${inhoud}
      </div>`;
  }

  /** De vaste kop van de modal: context, badges en de knoppen. */
  renderDetailKop() {
    const d = this.selected;
    const r = d?.recording;
    const t = d?.timeline;
    return html`
      <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:16px;
                  padding:20px 24px 16px; border-bottom:1px solid var(--mv-border,#e9ecef);">
        <div style="display:flex; flex-direction:column; gap:8px; min-width:0;">
          <div style="display:flex; align-items:baseline; gap:12px; flex-wrap:wrap;">
            <h2 style="margin:0; font-size:1.12rem; font-weight:700; color:var(--mv-text);">Pijplijn-detail</h2>
            ${r ? html`<span style="font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">
              ${[d.company?.name, r.project_name || r.project_id, r.uploaded_by_email, this.fmtDate(t?.created_at)].filter(Boolean).join(' · ')}
            </span>` : ''}
          </div>
          ${r ? html`
            <div style="display:flex; gap:8px; align-items:center; flex-wrap:wrap;">
              ${this.statusBadge(d.run ? d.run.status : r.status, t?.sec_total != null && t.sec_total > 60)}
              ${this.analysisBadge(this._profileOf(r))}
              ${d.run ? html`<span title=${`Logboek-entry van ${this.fmtDate(d.run.started_at)}`}
                style="display:inline-flex; align-items:center; padding:3px 10px; border-radius:999px; font-size:0.76rem;
                       font-weight:700; background:var(--mv-bg,#f1f3f5); color:var(--mv-text-sub,#6c757d);">${this.triggerLabel(d.run.trigger)}</span>` : ''}
              ${d.meting?.isChild ? html`<span title="Het boorprofiel is gemaakt op het gecombineerde transcript van de hele meting"
                style="display:inline-flex; align-items:center; padding:3px 10px; border-radius:999px; font-size:0.76rem;
                       font-weight:700; background:rgba(59,130,246,0.12); color:#1d4ed8;">Extra opname</span>` : ''}
              ${d.run ? html`<span style="font-size:0.76rem; color:var(--mv-text-sub,#6c757d); font-family:ui-monospace,Menlo,monospace;">run #${d.run.id}</span>` : ''}
            </div>
            ${r.deleted ? html`
              <div style="padding:8px 12px; border-radius:8px; font-size:0.82rem; line-height:1.45;
                          background:rgba(245,158,11,0.10); border:1px solid rgba(245,158,11,0.35); color:var(--mv-text);">
                De opname is verwijderd, bijvoorbeeld samen met zijn project. De audio, het transcript en de boringen bestaan
                niet meer; wat de verwerking zelf bewaarde (berichten aan het model, uitvoer, tijden, kosten) is hier nog te zien.
              </div>` : ''}` : ''}
        </div>
        <div style="display:flex; gap:8px; align-items:center;">
          ${d?.recording?.deleted ? '' : d?._toegang === 'meekijken' ? html`<span title="Je bent geen lid van dit bedrijf; het heeft meekijken door systeembeheerders aangezet. Bekijken mag, opnieuw verwerken niet."
            style="padding:3px 10px; border-radius:999px; font-size:0.76rem; font-weight:700; background:rgba(107,114,128,0.14); color:var(--mv-text);">Meekijken</span>` : html`
          <button @click=${() => this.reprocessJob()}
            ?disabled=${this.reprocessing || this.detailLoading || !d?.transcript}
            title=${d?.recording?.deleted ? 'De opname bestaat niet meer' : d?.transcript ? 'Verwerk het transcript opnieuw met AI' : 'Geen transcript om te verwerken'}
            style="display:flex; align-items:center; gap:6px; padding:7px 12px; border-radius:8px; border:1px solid var(--mv-border);
                   background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; font-size:0.82rem; cursor:pointer;
                   white-space:nowrap; ${this.reprocessing || !d?.transcript ? 'opacity:0.6; cursor:default;' : ''}">
            ${unsafeSVG(lucideIcon(RefreshCw, 15))} ${this.reprocessing ? 'Bezig…' : 'Opnieuw verwerken'}
          </button>`}
          <button @click=${this.closeDetail}
            style="width:34px; height:34px; border:none; background:none; font-size:1.5rem; line-height:1;
                   color:var(--mv-text-sub,#6c757d); cursor:pointer;">&times;</button>
        </div>
      </div>`;
  }

  /**
   * De rode melding onder de kop, met een snelkoppeling naar de stap die faalde.
   *
   * De reden van de RUN gaat voor die van de opname. `recording.error_message` wordt bij een
   * geslaagde herverwerking leeggemaakt, dus bij een mislukte run die later alsnog goed ging
   * stond er een rode pil 'Fout' naast 'AI processing completed' en verder niets. De run bewaart
   * zijn eigen reden en die blijft staan.
   */
  /** Het label voor een opname die de hallucinatie preventie heeft tegengehouden. */
  _preventieBadge(reden = 'de hallucinatie preventie hield deze opname tegen') {
    return html`<span title=${`Niet verwerkt — ${reden}`}
      style="display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:6px; font-size:0.72rem;
             font-weight:700; color:#92400e; background:rgba(245,158,11,0.14); border:1px solid rgba(245,158,11,0.35); white-space:nowrap;"
      >🛡 Hallucinatie preventie</span>`;
  }

  renderDetailFout() {
    const d = this.selected;
    if (!d?.recording) return '';
    const uitleg = foutUitleg(d.run?.error_message) || foutUitleg(d.recording.error_message);
    if (!uitleg) return '';
    // Hallucinatie preventie staat bij stap 2 (Transcriptie), waar hij hoort: zie _blokPreventie.
    if (uitleg.preventie) return '';
    const mislukt = stappen(d).find((s) => s.staat === 'fout');
    return html`
      <div style="margin:12px 24px 0; padding:10px 14px; border-radius:8px; background:rgba(220,53,69,0.08);
                  border:1px solid rgba(220,53,69,0.25); display:flex; justify-content:space-between; gap:12px;
                  align-items:center; flex-wrap:wrap;">
        <div style="display:flex; flex-direction:column; gap:2px; min-width:0;">
          <span style="font-weight:700; font-size:0.85rem; color:var(--mv-danger,#dc3545);">${uitleg.kort}</span>
          ${uitleg.hint ? html`<span style="font-size:0.78rem; color:var(--mv-text-sub,#4b5563);">${uitleg.hint}</span>` : ''}
        </div>
        ${mislukt ? html`<a href="#" @click=${(e) => { e.preventDefault(); this.step = mislukt.id; this.tab = 'pogingen'; }}
          style="font-size:0.82rem; font-weight:600; white-space:nowrap;">Bekijk pogingen</a>` : ''}
      </div>`;
  }

  render() {
    return html`
      <div class="dashboard-header" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1rem; flex-wrap:wrap; gap:1rem;">
        <div>
          <h1 style="margin:0; font-size:1.5rem; font-weight:700; color:var(--mv-text);">Analysis</h1>
        </div>
        <button @click=${this.load} ?disabled=${this.loading} style="display:flex; align-items:center; gap:8px; padding:8px 14px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-surface,#fff); color:var(--mv-text); font-weight:600; cursor:pointer;">
          ${unsafeSVG(lucideIcon(RefreshCw, 16))} Vernieuwen
        </button>
      </div>

      ${this.error ? html`<div style="padding:10px 14px; border-radius:8px; background:rgba(220,53,69,0.1); color:var(--mv-danger,#dc3545); margin-bottom:1rem;">${this.error}</div>` : ''}

      <div style="display:flex; gap:10px; padding:12px 14px; border-radius:10px; background:rgba(59,130,246,0.08); border:1px solid rgba(59,130,246,0.25); color:var(--mv-text); margin-bottom:1rem; font-size:0.88rem;">
        <span style="flex-shrink:0;">${unsafeSVG(lucideIcon(Info, 18))}</span>
        <span>Je ziet hier de analyses van bedrijven waar je lid van bent, en van bedrijven die meekijken door systeembeheerders hebben aangezet (die staan als "meekijken" in de kolom Bedrijf). Bij meekijken kun je alles bekijken maar niet opnieuw verwerken.</span>
      </div>

      <div style="display:flex; gap:10px; flex-wrap:wrap; margin-bottom:1rem;">
        ${this.filterSelect('Bedrijf', this.companyFilter, this.companyOptions, (v) => this.onCompanyChange(v))}
        ${this.filterSelect('Project', this.projectFilter, this.projectOptions, (v) => { this.projectFilter = v; })}
        ${this.filterSelect('Gebruiker', this.userFilter, this.userOptions, (v) => { this.userFilter = v; })}
        <div style="min-width:180px;">
        <select .value=${this.statusFilter} @change=${(e) => { this.statusFilter = e.target.value; }}
          style=${this._selectStyle}>
          <option value="all">Alle statussen</option>
          <option value="processed">Verwerkt</option>
          <option value="error">Fout</option>
          <option value="transcribed">Getranscribeerd</option>
          <option value="uploaded">Geüpload</option>
          <option value="processing">Bezig</option>
        </select>
        </div>
        <div style="min-width:200px;">
        <select .value=${this.profileFilter} @change=${(e) => { this.profileFilter = e.target.value; }}
          style=${this._selectStyle}>
          <option value="all">Alle analyses</option>
          ${EXTRACTION_PROFILES.map(p => html`<option value=${p.id}>${p.label}</option>`)}
          ${Object.entries(AnalysisPage.AI_SOORTEN).map(([id, s]) => html`<option value=${id}>${s.label}</option>`)}
        </select>
        </div>
        <div style="min-width:170px;">
        <select .value=${this.periode} ?disabled=${this.loading} @change=${(e) => { this.periode = e.target.value; this.load(); }}
          style=${this._selectStyle}>
          ${AnalysisPage.PERIODES.map((p) => html`<option value=${p.id}>${p.label}</option>`)}
        </select>
        </div>
        <label style="display:flex; align-items:center; gap:6px; font-size:0.88rem; color:var(--mv-text); cursor:pointer;"
               title="De runs van /admin/validation staan standaard uit beeld: één testronde is tachtig rijen, en die verdrinken het echte werk. Zet aan om ze erbij te zien.">
          <input type="checkbox" .checked=${this.showValidation} ?disabled=${this.loading}
                 @change=${(e) => { this.showValidation = e.target.checked; this.load(); }}>
          Validatieset tonen
        </label>
      </div>

      ${this._renderTelling()}

      <mv-table
        .columns=${this.columns}
        .data=${this.filtered}
        paginate
        .pageSize=${50}
        .columnOptions=${AnalysisPage.COLUMN_OPTIONS}
        .visibleColumns=${this.visibleColumns}
        @columns-changed=${(e) => this._saveColumnPrefs(e.detail.visible)}
        emptyText=${this.loading ? 'Jobs laden…' : 'Geen jobs gevonden.'}
        @row-click=${(e) => this.openDetail(e.detail.row)}
      ></mv-table>

      ${this.selected ? html`
        <div @click=${(e) => e.target === e.currentTarget && this.closeDetail()}
          style="position:fixed; inset:0; background:rgba(17,24,39,0.42); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px;">
          <div style="background:var(--mv-surface,#fff); border-radius:16px; width:100%; max-width:1140px; height:88vh;
                      display:flex; flex-direction:column; overflow:hidden; box-shadow:0 12px 40px rgba(0,0,0,0.22);">
            ${this.selected.kind === 'ai' && this.selected.call ? html`
              ${this.renderAiKop()}
              ${this.renderAiDetail()}` : html`
              ${this.renderDetailKop()}
              ${this.renderDetailFout()}
              ${this.renderDetail()}`}
          </div>
        </div>
      ` : ''}
    `;
  }
}

customElements.define('analysis-page', AnalysisPage);
